import { useEffect, useLayoutEffect, useState, useRef } from 'react';

const ADD_TABS = [
  { id: 'pedal', label: 'Add Pedal' },
  { id: 'want', label: 'Add Want' },
];

/**
 * Mobile compact bar (centered Add Pedal/Add Want toggle) and desktop in-flow
 * header for AddPedalPage. Mirrors Match Opportunities scroll-reveal on mobile.
 */
export default function AddPedalMobileChromeHeader({ activeTab, onTabChange }) {
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeTab]);

  const renderCompactBar = () => (
    <div className="add-pedal-compact-bar-inner">
      <div
        className="add-pedal-view-toggle"
        role="tablist"
        aria-label="Add pedal or want"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: 'white',
          borderRadius: '9999px',
          padding: '4px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          border: '1px solid #e5e7eb',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        {ADD_TABS.map(({ id, label }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className="add-pedal-view-toggle-btn"
              onClick={() => onTabChange(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.5rem 1.5rem',
                borderRadius: '9999px',
                border: 'none',
                fontWeight: 700,
                fontSize: '1rem',
                fontFamily: 'Nunito, sans-serif',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                textAlign: 'center',
                transition: 'background-color 0.2s, color 0.2s',
                backgroundColor: isActive ? '#C89F3C' : 'transparent',
                color: isActive ? 'white' : '#6B7280',
                boxSizing: 'border-box',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="add-pedal-sticky-header">
      <style>{`
        .add-pedal-compact-bar-inner {
          max-width: 1100px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .add-pedal-sticky-header .add-pedal-header-expanded {
          display: block;
        }
        .add-pedal-sticky-header .add-pedal-mobile-chrome-slot {
          display: none;
        }
        .add-pedal-sticky-header .add-pedal-compact-bar-mobile-fixed {
          display: none;
        }
        @media (min-width: 769px) {
          .add-pedal-sticky-header .add-pedal-compact-bar {
            display: none !important;
          }
          .add-pedal-sticky-header .add-pedal-header-expanded {
            padding: 0 1rem 1rem 1rem;
          }
          .add-pedal-sticky-header .add-pedal-view-toggle {
            padding: 6px !important;
            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15) !important;
            border: 1px solid #d1d5db !important;
          }
          .add-pedal-sticky-header .add-pedal-view-toggle-btn {
            padding: 0.75rem 2.5rem !important;
            font-size: 1.125rem !important;
          }
        }
        @media (max-width: 768px) {
          .add-pedal-page-content {
            padding-top: 0 !important;
          }
          .add-pedal-sticky-header .add-pedal-header-expanded {
            display: none !important;
          }
          .add-pedal-sticky-header .add-pedal-mobile-chrome-slot {
            display: block !important;
            position: relative;
            flex-shrink: 0;
            background-color: #F7F3EB;
            overflow: hidden;
          }
          .add-pedal-sticky-header .add-pedal-compact-bar-inflow {
            display: block;
            padding: 1rem 1rem 1rem 1rem;
            background-color: #F7F3EB;
            box-sizing: border-box;
          }
          .add-pedal-sticky-header .add-pedal-compact-bar-inflow.is-suppressed {
            visibility: hidden;
            pointer-events: none;
          }
          .add-pedal-sticky-header .add-pedal-compact-bar-mobile-fixed {
            display: block;
          }
          .add-pedal-sticky-header .add-pedal-compact-bar {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 100;
            padding: 0.35rem 1rem 0.5rem 1rem;
            background-color: #F7F3EB;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            box-sizing: border-box;
            pointer-events: none;
            transform: translateY(-100%);
            transition: transform 0.2s ease;
          }
          .add-pedal-sticky-header .add-pedal-compact-bar.is-mobile-visible {
            display: block;
            transform: translateY(0);
            pointer-events: auto;
          }
          .add-pedal-sticky-header .add-pedal-view-toggle-btn {
            padding: 0.5rem 1.5rem !important;
            font-size: 1rem !important;
          }
        }
      `}</style>

      <div
        ref={mobileChromeSlotRef}
        className="add-pedal-mobile-chrome-slot"
        style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
      >
        <div
          ref={mobileChromeInflowRef}
          className={['add-pedal-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
        >
          {renderCompactBar()}
        </div>
      </div>

      <div
        className={[
          'add-pedal-compact-bar',
          showMobileChromeBar && 'is-mobile-visible',
        ].filter(Boolean).join(' ')}
      >
        <div className="add-pedal-compact-bar-mobile-fixed">
          {renderCompactBar()}
        </div>
      </div>

      <div className="add-pedal-header-expanded">
        {renderCompactBar()}
      </div>
    </div>
  );
}
